/* eslint-disable @typescript-eslint/no-use-before-define */
import { forwardRef, useEffect, useRef } from 'react'

import FileLayoutEditor from '@components/FileLayoutEditor/FileLayoutEditor'
import IconButton from '@components/IconButton/IconButton'
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons'
import { Stack, TextField, Typography } from '@mui/material'
import { useDialogs } from '@toolpad/core'
import errorShake from '@utils/animations'
import { Controller, FieldError, useFormContext, useWatch } from 'react-hook-form'

type Props = {
	name: string
	label: string
	fieldToWatch: string
	// eslint-disable-next-line react/require-default-props
	value?: string
	// eslint-disable-next-line react/require-default-props
	required?: boolean
	// eslint-disable-next-line react/require-default-props
	error?: FieldError
	// eslint-disable-next-line react/require-default-props
	onChange?: (value: string) => void
}

export default function FileLayout({ name, label, ...rest }: Props) {
	const { control } = useFormContext()

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<Input label={label} name={name} {...field} error={fieldState.error} {...rest} />
			)}
		/>
	)
}

// --------------------------------------------------------
// Rendered Input
// --------------------------------------------------------
const Input = forwardRef<HTMLDivElement, Props>((props, ref) => {
	const {
		label,
		name,
		onChange = null,
		error = null,
		value,
		fieldToWatch,
		required = false,
	} = props

	const dialogs = useDialogs()

	// Need to handle this way due to component not updating properly when in error state
	const { control } = useFormContext()
	const uploadedFiles = useWatch({
		control,
		name: fieldToWatch,
	})

	const fieldRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (error && fieldRef.current) {
			fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
	}, [error])

	const handleEdit = async () => {
		const editedInputLayout = await dialogs.open(FileLayoutEditor, {
			file: uploadedFiles[0],
			string: value || '',
		})
		if (editedInputLayout) handleChange(editedInputLayout)
	}

	const handleChange = (value: string) => {
		onChange(value)
	}

	return (
		<Stack
			sx={{
				'@keyframes shake': errorShake,
				animation: error ? 'shake 0.25s' : null,
			}}
			spacing={1}
			ref={fieldRef}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Typography variant="body1" color={error ? 'error.main' : 'text.primary'}>
					{required ? `${label} *` : label}
				</Typography>
				<IconButton
					icon={faPenToSquare}
					size="small"
					onClick={handleEdit}
					aria-label="reset"
					data-testid={`${name}-reset`}
					disabled={!uploadedFiles?.length}
				/>
			</Stack>
			<TextField
				size="small"
				multiline
				fullWidth
				minRows={5}
				maxRows={5}
				value={value}
				error={!!error}
				onChange={(e) => handleChange(e.target.value)}
				data-testid={name}
				ref={ref}
			/>
			{error && <Typography color="error.main">{error?.message}</Typography>}
		</Stack>
	)
})
